import { Button, Card, CardBody, CardHeader, Popover, PopoverContent, PopoverTrigger, Progress, RangeCalendar, Select, SelectItem, Skeleton, Tab, Tabs } from '@heroui/react'
import { parseDate } from '@internationalized/date'
import { useDateFormatter } from '@react-aria/i18n'
import { BarChart3, Calendar, Phone, PieChart as PieChartIcon, TrendingUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useToggle } from 'usehooks-ts'
import { presets } from '../Analytics/utils/rangePresets'
import ExtractionRateChart from './ExtractionRateChart'
import { useSchemaAnalytics, useSchemaTopValues } from './utils'
import VariableExtractionChart from './VariableExtractionChart'

function DateRangeFilter({ filters, setFilters }) {
  const [selectedPreset, setSelectedPreset] = useState('lastWeek')
  const [isCalendarOpen, toggleCalendar] = useToggle(false)
  const formatter = useDateFormatter({ dateStyle: 'short' })

  const handlePresetSelection = (preset) => {
    setSelectedPreset(preset)
    if (preset === 'custom') {
      toggleCalendar()
    }
    else if (presets[preset]) {
      setFilters(presets[preset])
    }
  }

  const handleDateChange = (value) => {
    const start = value.start.toDate()
    const end = value.end.toDate()
    const diffDays = (end - start) / (1000 * 60 * 60 * 24)
    if (diffDays > 60) {
      toast.error('Date range should not exceed 60 days')
    }
    else {
      setSelectedPreset('custom')
      setFilters(value)
      toggleCalendar()
    }
  }

  const customDateRange = useMemo(() => {
    return formatter.formatRange(
      filters.start.toDate(),
      filters.end.toDate(),
    )
  }, [filters.start, filters.end, formatter])

  return (
    <div className="flex items-center gap-3">
      <Tabs
        variant="underlined"
        radius="none"
        size="sm"
        aria-label="Date Range Presets"
        selectedKey={selectedPreset}
        onSelectionChange={handlePresetSelection}
      >
        <Tab key="today" title="Today" />
        <Tab key="lastWeek" title="Last week" />
        <Tab key="last2Months" title="Last 2 months" />
        <Tab
          key="custom"
          title={(
            <span className="flex items-center gap-2">
              <Calendar size={16} className="text-gray-500" />
              {selectedPreset === 'custom' && (
                <span className="truncate font-mono text-gray-500">{customDateRange}</span>
              )}
            </span>
          )}
        />
      </Tabs>
      <Popover isOpen={isCalendarOpen} onOpenChange={toggleCalendar} placement="bottom" classNames={{ content: 'rounded-none p-0 border-2 border-gray-200' }}>
        <PopoverTrigger className="w-0 min-w-0 px-0">
          <Button className="invisible w-0 min-w-0" />
        </PopoverTrigger>
        <PopoverContent>
          <RangeCalendar
            visibleMonths={2}
            radius="none"
            classNames={{
              base: 'rounded-none',
            }}
            value={{
              start: parseDate(filters.start.toString()),
              end: parseDate(filters.end.toString()),
            }}
            onChange={handleDateChange}
            maxValue={presets.today.end}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

function StatCard({ title, value, subtitle, isLoading, icon: Icon }) {
  return (
    <Card classNames={{
      base: 'h-full border-2 grow border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className={`
        flex items-start justify-between gap-4 border-b border-gray-200
      `}
      >
        <div>
          <p className="font-mono text-xs text-gray-500 uppercase">{title}</p>
          {isLoading
            ? (
                <Skeleton className="mt-1 h-8 w-24 rounded-none" />
              )
            : (
                <p className="font-taurus text-2xl font-bold">{value}</p>
              )}
        </div>
        {Icon && <Icon size={14} className="text-gray-400" />}
      </CardHeader>
      {subtitle && (
        <CardBody className="pt-2">
          <p className="text-xs text-gray-500">{subtitle}</p>
        </CardBody>
      )}
    </Card>
  )
}

function CategoricalDistribution({ distributions, variableMetadata, isLoading }) {
  const [selectedVariable, setSelectedVariable] = useState('')

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-none" />
  }

  const categoricalVars = distributions?.filter(d =>
    distributions.some(dist => dist.variable_name === d.variable_name),
  ).map(d => d.variable_name).filter((v, i, a) => a.indexOf(v) === i) || []

  if (!categoricalVars.length) {
    return (
      <Card classNames={{
        base: 'border-2 border-gray-200 shadow-none rounded-none h-full',
      }}
      >
        <CardBody className={`
          flex h-full min-h-[300px] items-center justify-center
        `}
        >
          <div className="text-center">
            <h1 className={`
              mb-2 text-2xl font-semibold tracking-tighter text-gray-400
            `}
            >
              No categorical variables yet.
            </h1>
          </div>
        </CardBody>
      </Card>
    )
  }

  const selectedData = distributions?.filter(d =>
    d.variable_name === (selectedVariable || categoricalVars[0]),
  ) || []

  const currentVariable = selectedVariable || categoricalVars[0]
  const metadata = variableMetadata?.[currentVariable]

  return (
    <Card classNames={{
      base: 'border-2 border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className="flex flex-col gap-2 border-b border-gray-200">
        <div className="flex w-full items-center justify-between">
          <h4 className="font-taurus text-lg font-semibold">Categorical Distributions</h4>
          <PieChartIcon size={20} className="text-gray-400" />
        </div>
        <Select
          selectedKeys={[selectedVariable || categoricalVars[0]]}
          onSelectionChange={keys => setSelectedVariable(Array.from(keys)[0])}
          className="max-w-xs"
          size="sm"
          variant="bordered"
          radius="none"
          classNames={{
            trigger: 'border-gray-200',
            value: 'font-mono text-sm',
          }}
        >
          {categoricalVars.map(varName => (
            <SelectItem key={varName}>
              {varName}
            </SelectItem>
          ))}
        </Select>
        {metadata?.description && (
          <p className="text-xs text-gray-500">{metadata.description}</p>
        )}
      </CardHeader>
      <CardBody>
        <div className="flex flex-col gap-y-2">
          {selectedData.map((item, index) => {
            const total = selectedData.reduce((sum, d) => sum + d.count, 0)
            const percentage = (item.count / total * 100).toFixed(1)

            return (
              <div key={index} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-mono text-sm">{item.category_value || '(empty)'}</span>
                    <span className="font-mono text-sm text-gray-600">
                      {item.count}
                      {' '}
                      (
                      {percentage}
                      %)
                    </span>
                  </div>
                  <Progress
                    value={Number.parseFloat(percentage)}
                    size="sm"
                    color="primary"
                    classNames={{
                      base: 'border border-gray-200',
                      indicator: 'bg-primary-500',
                      track: 'bg-gray-100',
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardBody>
    </Card>
  )
}

function ArrayStats({ arrayStats, isLoading }) {
  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-none" />
  }

  if (!arrayStats?.length) {
    return (
      <Card classNames={{
        base: 'border-2 border-gray-200 shadow-none rounded-none h-full',
      }}
      >
        <CardBody className="flex min-h-[200px] items-center justify-center">
          <div className="text-center">
            <h1 className={`
              text-2xl font-semibold tracking-tighter text-gray-400
            `}
            >
              No array variables yet.
            </h1>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      {arrayStats.map(stat => (
        <Card
          key={stat.variable_name}
          classNames={{
            base: 'border-2 border-gray-200 shadow-none rounded-none',
          }}
        >
          <CardHeader className="border-b border-gray-200">
            <h4 className="font-mono font-medium">{stat.variable_name}</h4>
          </CardHeader>
          <CardBody className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase">Average Items</p>
              <p className="font-taurus text-lg font-semibold">{stat.avg_items?.toFixed(1) || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Range</p>
              <p className="font-taurus text-lg font-semibold">
                {stat.min_items || 0}
                {' '}
                -
                {' '}
                {stat.max_items || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Empty Arrays</p>
              <p className="font-taurus text-lg font-semibold">{stat.empty_arrays || 0}</p>
            </div>
          </CardBody>
        </Card>
      ))}
    </>
  )
}

function NumericStats({ numericStats, isLoading }) {
  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-none" />
  }

  if (!numericStats?.length) {
    return (
      <Card classNames={{
        base: 'border-2 border-gray-200 shadow-none rounded-none h-full',
      }}
      >
        <CardBody className="flex min-h-[200px] items-center justify-center">
          <div className="text-center">
            <h1 className={`
              text-2xl font-semibold tracking-tighter text-gray-400
            `}
            >
              No numeric variables yet.
            </h1>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      {numericStats.map(stat => (
        <Card
          key={stat.variable_name}
          classNames={{
            base: 'border-2 border-gray-200 shadow-none rounded-none',
          }}
        >
          <CardHeader className="border-b border-gray-200">
            <h4 className="font-mono font-medium">{stat.variable_name}</h4>
          </CardHeader>
          <CardBody className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase">Average</p>
              <p className="font-taurus text-lg font-semibold">{stat.avg_value?.toFixed(2) || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Median</p>
              <p className="font-taurus text-lg font-semibold">{stat.median_value?.toFixed(2) || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Range</p>
              <p className="font-mono text-sm font-semibold">
                {stat.min_value?.toFixed(2) || 0}
                {' '}
                -
                {stat.max_value?.toFixed(2) || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Std Dev</p>
              <p className="font-taurus text-lg font-semibold">{stat.std_dev?.toFixed(2) || 0}</p>
            </div>
          </CardBody>
        </Card>
      ))}
    </>
  )
}

function TopValuesDisplay({ schemaId, categoricalVariables, dateRange, isLoading }) {
  const [selectedVariable, setSelectedVariable] = useState('')

  // Get the first categorical variable as default
  const defaultVariable = categoricalVariables?.[0] || ''
  const currentVariable = selectedVariable || defaultVariable

  const { data: topValues, isLoading: isLoadingTopValues } = useSchemaTopValues(
    schemaId,
    currentVariable,
    10,
    dateRange,
  )

  if (isLoading || !categoricalVariables?.length) {
    return null
  }

  return (
    <Card classNames={{
      base: 'border-2 border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className="flex flex-col gap-2 border-b border-gray-200">
        <div className="flex w-full items-center justify-between">
          <h4 className="font-taurus text-lg font-semibold">Top Extracted Values</h4>
          <TrendingUp size={20} className="text-gray-400" />
        </div>
        <Select
          selectedKeys={[currentVariable]}
          onSelectionChange={keys => setSelectedVariable(Array.from(keys)[0])}
          className="max-w-xs"
          size="sm"
          variant="bordered"
          radius="none"
          classNames={{
            trigger: 'border-gray-200',
            value: 'font-mono text-sm',
          }}
        >
          {categoricalVariables.map(varName => (
            <SelectItem key={varName}>
              {varName}
            </SelectItem>
          ))}
        </Select>
      </CardHeader>
      <CardBody>
        {isLoadingTopValues
          ? (
              <Skeleton className="h-48 w-full rounded-none" />
            )
          : (
              <div className="flex flex-col gap-y-3">
                {topValues?.map((item, index) => (
                  <div
                    key={index}
                    className={`
                      flex items-center justify-between gap-4 border-b
                      border-gray-100 pb-2
                      last:border-0
                    `}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="shrink-0 font-mono text-sm text-gray-500">
                        #
                        {index + 1}
                      </span>
                      <span className="truncate font-mono text-sm" title={item.extracted_value}>
                        {item.extracted_value || '(empty)'}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-mono text-sm font-medium">{item.count}</span>
                      <span className="font-mono text-xs text-gray-500">
                        (
                        {item.unique_calls}
                        {' '}
                        calls)
                      </span>
                    </div>
                  </div>
                ))}
                {!topValues?.length && (
                  <div className="py-8 text-center">
                    <h1 className={`
                      text-xl font-semibold tracking-tighter text-gray-600
                    `}
                    >
                      No values extracted yet.
                    </h1>
                  </div>
                )}
              </div>
            )}
      </CardBody>
    </Card>
  )
}

export default function SchemaAnalysis() {
  const [searchParams] = useSearchParams()
  const schemaId = searchParams.get('schema')

  const [filters, setFilters] = useState({
    ...presets.lastWeek,
  })

  const { data: analytics, isLoading, isFetching } = useSchemaAnalytics(schemaId, filters)

  if (!schemaId) {
    return null
  }

  const totalVariables = analytics?.variable_stats?.length || 0
  const avgExtractionRate = analytics?.variable_stats?.reduce(
    (sum, v) => sum + v.extraction_rate,
    0,
  ) / totalVariables || 0

  return (
    <div className="mt-3 flex w-full flex-col gap-6">

      <div className="flex flex-row items-end gap-3">
        <StatCard
          title="Total Variables"
          value={totalVariables}
          isLoading={isLoading || isFetching}
          icon={BarChart3}
        />
        <StatCard
          title="Avg Extraction Rate"
          value={`${(avgExtractionRate * 100).toFixed(1)}%`}
          isLoading={isLoading || isFetching}
          icon={TrendingUp}
        />
        <StatCard
          title="Total Calls"
          value={analytics?.total_calls || 0}
          isLoading={isLoading || isFetching}
          icon={Phone}
        />

        <DateRangeFilter
          filters={filters}
          setFilters={setFilters}
        />
      </div>

      <Tabs placement="start" variant="underlined" radius="none" classNames={{ panel: 'w-full pl-10' }}>
        <Tab key="overview" title="Overview">
          <VariableExtractionChart
            variables={analytics?.variable_stats}
            isLoading={isLoading || isFetching}
          />
        </Tab>

        <Tab key="distributions" title="Distributions">
          <div className="flex flex-col gap-y-6">
            <div className={`
              grid grid-cols-1 gap-6
              md:grid-cols-2
            `}
            >
              <CategoricalDistribution
                distributions={analytics?.categorical_distributions}
                variableMetadata={analytics?.variable_metadata}
                isLoading={isLoading || isFetching}
              />
              <TopValuesDisplay
                schemaId={schemaId}
                categoricalVariables={[...new Set(analytics?.categorical_distributions?.map(d => d.variable_name) || [])]}
                dateRange={filters}
                isLoading={isLoading || isFetching}
              />
            </div>
            <div className={`
              grid grid-cols-1 gap-4
              md:grid-cols-2
            `}
            >
              <NumericStats
                numericStats={analytics?.numeric_stats}
                isLoading={isLoading || isFetching}
              />
              <ArrayStats
                arrayStats={analytics?.array_stats}
                isLoading={isLoading || isFetching}
              />
            </div>
          </div>
        </Tab>

        <Tab key="trends" title="Trends">
          <ExtractionRateChart
            schemaId={schemaId}
            dateRange={filters}
            isLoading={isLoading || isFetching}
          />
        </Tab>
      </Tabs>
    </div>
  )
}
